import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "../../lib/utils.ts";

/**
 * The design's button shapes. Colours and states come from the component
 * classes in styles.css (`.primary-btn`, `.surface-btn`, `.ghost-btn`); this
 * only adds layout. Every variant is at least 44px tall — thumbs, not cursors.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 select-none disabled:pointer-events-none",
  {
    variants: {
      variant: {
        /** "Log a bottle", "Save …" — the one solid accent button. */
        primary: "primary-btn heading min-h-[52px] px-4 text-[17px]",
        /** Paper-ground bordered button (Login, Settings actions). */
        secondary: "surface-btn heading min-h-[48px] justify-center px-4 text-[16px]",
        /** Text-only: Cancel, Undo, links. */
        ghost: "ghost-btn min-h-[44px] px-2 text-[14px]",
        /** Quick-action tile: label + sub-line, left aligned. */
        tile: "surface-btn min-h-[64px] flex-col !items-start justify-center gap-0.5 px-3.5",
        /** Full-width choice row in a sheet ("Wet", "Gas drops"). */
        choice: "surface-btn heading min-h-[56px] w-full !justify-start px-4 text-[18px]",
        /** Preset chip ("2.5 oz"). */
        chip: "surface-btn min-h-[44px] flex-1 justify-center text-[15px]",
        /** The 56px – / + stepper. */
        stepper: "surface-btn size-14 shrink-0 justify-center text-[30px] leading-none",
        /** 44px icon button in the header. */
        icon: "ghost-btn size-11 justify-center text-ink",
      },
    },
    defaultVariants: { variant: "secondary" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, type = "button", ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant }), className)}
      {...props}
    />
  ),
);
Button.displayName = "Button";

export { buttonVariants };
